import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { signInWithGoogle } from '../api/admin';
import { ApiError, setToken, setUnauthorizedHandler } from '../api/client';
import type { AdminSession } from '../api/types';
import { clearStoredSession, readStoredSession, storeSession } from './session';

/**
 * The admin session. It survives a reload of this tab and dies with the tab
 * (02-requirements.md §Admin Authentication, revised 2026-08-16): the token is
 * mirrored into `sessionStorage`, never `localStorage`, and the API's 8 h TTL
 * is unchanged — `session.ts` states what that costs.
 *
 * A 401 from any request ends the session everywhere at once, stored copy
 * included, which is why the client's unauthorized handler is registered here
 * rather than screen by screen.
 */

/**
 * Read once, at module load: the token has to be on the client before the first
 * query runs, and a `useEffect` would fire after the first render — long enough
 * for `RequireAdmin` to bounce a reload to Login.
 */
const restored = readStoredSession();
if (restored) setToken(restored.token);
export interface AuthState {
  admin: AdminSession['admin'] | null;
  signingIn: boolean;
  error: string | null;
  /** Trades the Google ID token from the sign-in button for the API's session. */
  signIn(idToken: string): Promise<void>;
  /** For a failure that happens before the API is asked (the Google script). */
  failSignIn(code: string): void;
  signOut(): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [admin, setAdmin] = useState<AdminSession['admin'] | null>(restored?.admin ?? null);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signOut = useCallback(() => {
    setToken(null);
    clearStoredSession();
    setAdmin(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      // The stored copy goes with it: a token the API refused must not be
      // handed back by the next reload.
      clearStoredSession();
      setAdmin(null);
      setError('session_expired');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const signIn = useCallback(async (idToken: string) => {
    setSigningIn(true);
    setError(null);
    try {
      const session = await signInWithGoogle(idToken);
      // Order matters: the token has to be readable before any query runs, and
      // setting the admin is what unblocks the router.
      setToken(session.token);
      storeSession({ token: session.token, admin: session.admin });
      setAdmin(session.admin);
    } catch (cause) {
      setToken(null);
      clearStoredSession();
      setAdmin(null);
      // The API's own keys, untranslated — the screen maps them to pt-BR copy.
      setError(cause instanceof ApiError ? cause.code : 'network_error');
    } finally {
      setSigningIn(false);
    }
  }, []);

  const failSignIn = useCallback((code: string) => setError(code), []);

  const value = useMemo<AuthState>(
    () => ({ admin, signingIn, error, signIn, failSignIn, signOut }),
    [admin, signingIn, error, signIn, failSignIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth used outside AuthProvider');
  return value;
}
